/**
 * The console's session, held by the server.
 *
 * WHAT THIS REPLACED. Every load read `sk_live_…` out of `localStorage` and sent it as a bearer
 * token. The comment defending that arrangement argued against the wrong threat — "never in a
 * URL, because a key in a query string ends up in history and referrers" — which is true and is
 * not the exposure that matters. Anything executing on this origin could read it: an XSS, a
 * compromised dependency, a browser extension. It had no expiry, no rotation, and no revocation
 * short of minting a new tenant.
 *
 * For this product in particular that is not a preference. authoxi separates a short-lived
 * passport that can only ask from the `sk_live_` that can issue, and the rule it sells is that
 * the issuing credential never ships inside an agent. A console is an agent in every way that
 * matters.
 *
 * WHAT IT IS NOW. The key is posted once to `/v1/bff/session`. The server validates it, keeps
 * it, and returns an `HttpOnly` cookie the page cannot read. Every later call goes to
 * `/v1/bff/api/*` on this same origin and the server attaches the credential.
 *
 * There is no token in this file, and there is nowhere in the console to put one.
 */

const BFF = "/v1/bff";

export class SessionError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SessionError";
  }
}

export interface SessionInfo {
  tenant_id: string;
  actor: string | null;
  /** Seconds until this session stops resolving, whichever timeout comes first. */
  expires_in: number;
}

async function unwrap<T>(resp: Response, what: string): Promise<T> {
  const text = await resp.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    throw new SessionError(resp.status, "bad_response", `${what}: not JSON`);
  }
  if (!resp.ok) {
    const err = (parsed as { error?: { code?: string; message?: string } })?.error;
    throw new SessionError(resp.status, err?.code ?? String(resp.status), err?.message ?? what);
  }
  return parsed as T;
}

/**
 * `credentials: "same-origin"` on every call.
 *
 * Not `include`, deliberately. The cookie is `SameSite=Strict` and `__Host-`-prefixed, so it is
 * same-origin by construction; asking for `include` would only widen what the browser is willing
 * to attach and would quietly stop matching the server's own posture.
 */
function withCookie(init: RequestInit = {}): RequestInit {
  return { ...init, credentials: "same-origin" };
}

/** Trade the key for a cookie. The only moment the key exists in this tab. */
export async function signIn(secretKey: string, actor?: string): Promise<SessionInfo> {
  const resp = await fetch(
    `${BFF}/session`,
    withCookie({
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ secret_key: secretKey, actor: actor || undefined }),
    }),
  );
  return unwrap<SessionInfo>(resp, "sign in");
}

/**
 * Are we signed in? Asked of the server, because the browser now holds nothing it can inspect.
 *
 * Returns null rather than throwing on 401: "not signed in" is the ordinary first answer on a
 * fresh browser, not an error the caller should have to catch.
 */
export async function currentSession(): Promise<SessionInfo | null> {
  const resp = await fetch(`${BFF}/session`, withCookie({ headers: { accept: "application/json" } }));
  if (resp.status === 401) return null;
  return unwrap<SessionInfo>(resp, "read session");
}

/** Sign out. The server drops the session; clearing the cookie alone would be theatre. */
export async function signOut(): Promise<void> {
  await fetch(`${BFF}/session`, withCookie({ method: "DELETE" })).catch(() => {
    // A failed sign-out must still clear the UI. The session expires on its own, and leaving
    // somebody staring at a console they think they left is the worse outcome.
  });
}

/** Where console API calls go now. Every path is allowlisted server-side. */
export const BFF_API_BASE = `${BFF}/api`;
